import * as React from "react";
import { useTranslation } from 'react-i18next';
import Field from "components/field";
import { Con, List, ListCategory, AmountRange, BalanceRange, Props } from "./style-and-types";

type FilterParts = {
  amountMin: string;
  amountMax: string;
  balanceMin: string;
  balanceMax: string;
  beginDate: string;
  endDate: string;
  selectedPaymentMethod: string;
};

function mergeFiltersIntoSearch(
  baseSearch: Record<string, unknown>,
  parts: FilterParts,
  resetPage: boolean
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...baseSearch };
  const filterKeys = [
    "amountMin",
    "amountMax",
    "balanceMin",
    "balanceMax",
    "beginDate",
    "endDate",
    "paymentMethod",
  ] as const;
  filterKeys.forEach((k) => {
    delete out[k];
  });

  if (parts.amountMin) out.amountMin = [parts.amountMin];
  if (parts.amountMax) out.amountMax = [parts.amountMax];
  if (parts.balanceMin) out.balanceMin = [parts.balanceMin];
  if (parts.balanceMax) out.balanceMax = [parts.balanceMax];
  if (parts.beginDate) out.beginDate = [parts.beginDate];
  if (parts.endDate) out.endDate = [parts.endDate];
  if (parts.selectedPaymentMethod && parts.selectedPaymentMethod !== "All") {
    out.paymentMethod = [parts.selectedPaymentMethod];
  }

  if (resetPage) out.page = ["1"];
  return out;
}

export function PaymentFilter({
  query,
  count,
  handleStatusClick,
  selected,
  search
}: Props) {
  const { t } = useTranslation();
  const tBase = "views.payments.paymentFilter";
  const tr = React.useCallback((key: string) => t(`${tBase}.${key}`), [t, tBase]);

  const [amountMin, setAmountMin] = React.useState("");
  const [amountMax, setAmountMax] = React.useState("");
  const [balanceMin, setBalanceMin] = React.useState("");
  const [balanceMax, setBalanceMax] = React.useState("");
  const [beginDate, setBeginDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState("All");

  const searchRef = React.useRef(search);
  searchRef.current = search;

  React.useLayoutEffect(() => {
    setAmountMin(query?.amountMin?.[0] || "");
    setAmountMax(query?.amountMax?.[0] || "");
    setBalanceMin(query?.balanceMin?.[0] || "");
    setBalanceMax(query?.balanceMax?.[0] || "");
    setBeginDate(query?.beginDate?.[0] || "");
    setEndDate(query?.endDate?.[0] || "");
    setSelectedPaymentMethod(query?.paymentMethod?.[0] || "All");
  }, [query]);

  const commitFiltersToUrl = React.useCallback(
    (override: Partial<FilterParts>, resetPage = true) => {
      const parts: FilterParts = {
        amountMin: override.amountMin ?? amountMin,
        amountMax: override.amountMax ?? amountMax,
        balanceMin: override.balanceMin ?? balanceMin,
        balanceMax: override.balanceMax ?? balanceMax,
        beginDate: override.beginDate ?? beginDate,
        endDate: override.endDate ?? endDate,
        selectedPaymentMethod:
          override.selectedPaymentMethod ?? selectedPaymentMethod,
      };
      const merged = mergeFiltersIntoSearch(
        searchRef.current.search as Record<string, unknown>,
        parts,
        resetPage
      );
      searchRef.current.update(merged);
    },
    [
      amountMin,
      amountMax,
      balanceMin,
      balanceMax,
      beginDate,
      endDate,
      selectedPaymentMethod,
    ]
  );

  const handleEndDateChange = React.useCallback(
    (newEndDate: string) => {
      let nextBegin = beginDate;
      if (beginDate && newEndDate && newEndDate < beginDate) {
        nextBegin = newEndDate;
        setBeginDate(newEndDate);
      }
      setEndDate(newEndDate);
      commitFiltersToUrl(
        { beginDate: nextBegin, endDate: newEndDate },
        true
      );
    },
    [beginDate, commitFiltersToUrl]
  );

  const populateStatus = React.useMemo(() => {
    const statuses = ["Voided", "Deposit", "Payment"];
    return statuses.map((status) => {
      const findCount = count?.find((count) => count.status === status) || { count: 0 };

      return (
        <List
          key={status}
          clicked={status === selected}
          onClick={() => {
            handleStatusClick(status);
          }}
        >
          <div>{tr(status)}</div>
          <div>{findCount.count}</div>
        </List>
      );
    });
  }, [count, selected, handleStatusClick, tr]);

  return (
    <Con className="border-r">
      <ListCategory>
        <p>
          {tr("Status")}
        </p>
        <p onClick={() => handleStatusClick("showAll")} style={{ color: "#FF4F00", cursor: "pointer" }}>
          {tr("Show All")}
        </p>
      </ListCategory>
      {populateStatus}

    <p style={{ textAlign: "center" }}>
      {tr("Amount Range")}
    </p>

    <AmountRange>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Min")}
        value={amountMin}
        setValue={setAmountMin}
        onBlur={() => commitFiltersToUrl({ amountMin }, true)}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
      <p style={{ margin: "0 0.5rem", fontSize: "1.2rem", textAlign: "center" }}>-</p>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Max")}
        value={amountMax}
        setValue={setAmountMax}
        onBlur={() => commitFiltersToUrl({ amountMax }, true)}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
    </AmountRange>

    <p style={{ textAlign: "center" }}>
      {tr("Balance Range")}
    </p>

    <BalanceRange>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Min")}
        value={balanceMin}
        setValue={setBalanceMin}
        onBlur={() => commitFiltersToUrl({ balanceMin }, true)}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
      <p style={{ margin: "0 0.5rem", fontSize: "1.2rem", textAlign: "center" }}>-</p>
      <Field
        backgroundColorFocus="white"
        placeholder={tr("Max")}
        value={balanceMax}
        setValue={setBalanceMax}
        onBlur={() => commitFiltersToUrl({ balanceMax }, true)}
        style={{ width: "5rem" }}
        advancedNumericalInputHandling
        allowLeaders
      />
    </BalanceRange>


    <p style={{ textAlign: "center", marginTop: "1rem" }}>
      {tr("Entry Date Range")}
    </p>

    <div style={{ margin: "0.5rem 1rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <label style={{ fontSize: "0.9rem", fontWeight: "500" }}>{tr("Begin Date")}:</label>
        <input
          type="date"
          onChange={(e) => {
            const v = e.target.value;
            setBeginDate(v);
            commitFiltersToUrl({ beginDate: v }, true);
          }}
          value={beginDate || ""}
          style={{
            fontSize: "0.8rem",
            padding: "0.25rem",
            borderRadius: "4px",
            border: "1px solid #d6dbdf",
            backgroundColor: "white"
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <label style={{ fontSize: "0.9rem", fontWeight: "500" }}>{tr("End Date")}:</label>
        <input
          type="date"
          onChange={(e) => handleEndDateChange(e.target.value)}
          value={endDate || ""}
          style={{
            fontSize: "0.8rem",
            padding: "0.25rem",
            borderRadius: "4px",
            border: "1px solid #d6dbdf",
            backgroundColor: "white"
          }}
        />
      </div>
    </div>


    <p style={{ textAlign: "center", marginTop: "1rem" }}>
      {tr("Payment Method")}
    </p>

    <div style={{ margin: "0.5rem 1rem" }}>
      <select
        value={selectedPaymentMethod || "All"}
        onChange={(e) => {
          const v = e.target.value;
          setSelectedPaymentMethod(v);
          commitFiltersToUrl({ selectedPaymentMethod: v }, true);
        }}
        style={{
          width: "100%",
          padding: "0.5rem",
          borderRadius: "4px",
          border: "1px solid #d6dbdf",
          backgroundColor: "white",
          fontSize: "0.9rem"
        }}
      >
        <option value="All">{tr("All Payment Methods")}</option>
        <option value="StripeCard">{tr("Stripe (Card)")}</option>
        <option value="ManualCard">{tr("Manual (Card)")}</option>
        <option value="ManualCash">{tr("Manual (Cash)")}</option>
        <option value="ManualCheck">{tr("Manual (Check)")}</option>
        <option value="ManualDonation">{tr("Manual (Donation)")}</option>
        <option value="ManualWriteOff">{tr("Manual (Write-Off)")}</option>
        <option value="ManualFinancing">{tr("Manual (Financing)")}</option>
        <option value="ManualUSBankAccount">{tr("Manual (US Bank Account)")}</option>
        <option value="ManualOther">{tr("Manual (Other)")}</option>
      </select>
    </div>


    </Con>
  );
}

export default React.memo(PaymentFilter);
